/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Template Output Panel & Rich Markdown Preview Component (Task 10.2.6 & Phase 5 Task 5.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Copy,
  Check,
  Download,
  FileEdit,
  Square,
  Sparkles,
  FolderPlus,
  Save,
  X,
  CheckCircle2,
  Trash2,
  FileCode,
  FileText,
  Loader2,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { useEditorStore } from '../../stores/useEditorStore';
import { useWorkspaceStore } from '../../stores/useWorkspaceStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { useI18n } from '../../hooks/useI18n';
import { getApiBaseUrl } from '../../lib/api-client';
import { MarkdownRenderer } from '../chat/MarkdownRenderer';

export interface TemplateOutputPanelProps {
  content: string;
  isStreaming: boolean;
  tokensGenerated?: number;
  templateTitle?: string;
  templateId?: string;
  onStopStreaming?: () => void;
  onNavigateToEditor?: () => void;
  onClear?: () => void;
}

/**
 * Converts markdown text into structured semantic HTML for TipTap Editor consumption.
 */
function convertMarkdownToHtml(markdown: string): string {
  const blocks = markdown.split(/\n\n+/);
  return blocks
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return '';

      // Code blocks
      if (trimmed.startsWith('```') && trimmed.endsWith('```')) {
        const lines = trimmed.split('\n');
        const lang = lines[0].replace(/^```/, '').trim();
        const code = lines.slice(1, -1).join('\n');
        return `<pre><code class="language-${lang || 'plaintext'}">${code
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')}</code></pre>`;
      }

      // Headings
      if (trimmed.startsWith('#### ')) {
        return `<h4>${trimmed.slice(5)}</h4>`;
      }
      if (trimmed.startsWith('### ')) {
        return `<h3>${trimmed.slice(4)}</h3>`;
      }
      if (trimmed.startsWith('## ')) {
        return `<h2>${trimmed.slice(3)}</h2>`;
      }
      if (trimmed.startsWith('# ')) {
        return `<h1>${trimmed.slice(2)}</h1>`;
      }

      // Blockquote
      if (trimmed.startsWith('> ')) {
        const quoteContent = trimmed
          .split('\n')
          .map((line) => line.replace(/^>\s?/, ''))
          .join('<br/>');
        return `<blockquote><p>${quoteContent}</p></blockquote>`;
      }

      // Unordered list
      if (/^[-*]\s/.test(trimmed)) {
        const items = trimmed
          .split('\n')
          .map((line) => line.replace(/^[-*]\s+/, '').trim())
          .filter(Boolean)
          .map((item) => `<li>${item}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }

      // Ordered list
      if (/^\d+\.\s/.test(trimmed)) {
        const items = trimmed
          .split('\n')
          .map((line) => line.replace(/^\d+\.\s+/, '').trim())
          .filter(Boolean)
          .map((item) => `<li>${item}</li>`)
          .join('');
        return `<ol>${items}</ol>`;
      }

      // Standard paragraph
      return `<p>${trimmed.replace(/\n/g, '<br/>')}</p>`;
    })
    .filter(Boolean)
    .join('');
}

export const TemplateOutputPanel: React.FC<TemplateOutputPanelProps> = ({
  content,
  isStreaming,
  tokensGenerated = 0,
  templateTitle = 'New Content',
  templateId,
  onStopStreaming,
  onNavigateToEditor,
  onClear,
}) => {
  const [copied, setCopied] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [viewMode, setViewMode] = useState<'formatted' | 'raw'>('formatted');
  const [isSaving, setIsSaving] = useState(false);
  const [isSavedSuccess, setIsSavedSuccess] = useState(false);

  // Save Modal Form States
  const [docTitle, setDocTitle] = useState(templateTitle);
  const [docFolder, setDocFolder] = useState('root');
  const [docTags, setDocTags] = useState('AI Prompt, Generated');

  const { activeWorkspaceId } = useWorkspaceStore();
  const { accessToken } = useAuthStore();
  const { setTitle, setContent } = useEditorStore();
  const { t } = useI18n();

  const words = content.trim().split(/\s+/).filter(Boolean).length;

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 2500);
  };

  const handleCopy = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        showToast(t('templates.output.copySuccess'));
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // Clipboard write fallback
    }
  };

  const handleExport = (format: 'markdown' | 'text' | 'html') => {
    let output = content;
    let mimeType = 'text/markdown';
    let ext = 'md';

    if (format === 'text') {
      mimeType = 'text/plain';
      ext = 'txt';
    } else if (format === 'html') {
      mimeType = 'text/html';
      ext = 'html';
      const cleanBody = convertMarkdownToHtml(content);
      output = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${docTitle || templateTitle}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; line-height: 1.6; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1e293b; }
    h1, h2, h3, h4 { color: #0f172a; margin-top: 1.5em; }
    code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 0.9em; }
    pre { background: #0f172a; color: #f8fafc; padding: 16px; border-radius: 8px; overflow-x: auto; }
    blockquote { border-left: 4px solid #3b82f6; padding-left: 16px; color: #64748b; margin: 16px 0; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0; }
    th, td { border: 1px solid #cbd5e1; padding: 8px 12px; text-align: left; }
    th { background: #f8fafc; }
  </style>
</head>
<body>
  <article>
    <h1>${docTitle || templateTitle}</h1>
    ${cleanBody}
  </article>
</body>
</html>`;
    }

    const blob = new Blob([output], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(docTitle || templateTitle).toLowerCase().replace(/[\s/]+/g, '-') || 'document'}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const handleSendToTiptap = () => {
    const formattedHtml = convertMarkdownToHtml(content);
    setTitle(docTitle || templateTitle);
    setContent(formattedHtml);

    if (onNavigateToEditor) {
      onNavigateToEditor();
    }
  };

  const handleSaveDocument = async () => {
    setIsSaving(true);
    const draftKey = `sofia_doc_${Date.now()}`;
    const targetWorkspaceId = activeWorkspaceId || 'ws_default';
    const finalTitle = docTitle.trim() || templateTitle || 'Untitled Document';
    const folderValue = docFolder === 'root' ? null : docFolder;

    const payload = {
      id: draftKey,
      workspaceId: targetWorkspaceId,
      title: finalTitle,
      folderId: folderValue,
      tags: docTags.split(',').map((item) => item.trim()).filter(Boolean),
      content,
      templateId,
      type: 'article' as const,
      savedAt: Date.now(),
    };

    // 1. Try remote Cloudflare Edge API Worker if authenticated
    if (accessToken) {
      try {
        const baseUrl = getApiBaseUrl();
        await fetch(`${baseUrl}/api/v1/workspaces/${targetWorkspaceId}/documents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'x-workspace-id': targetWorkspaceId,
          },
          body: JSON.stringify({
            title: finalTitle,
            content,
            folderId: folderValue,
            type: 'article',
            templateId,
          }),
        });
      } catch {
        // Fallback to local storage on network/worker errors
      }
    }

    // 2. Always persist a local backup copy to localStorage
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(draftKey, JSON.stringify(payload));
      } catch {
        // Storage quota exceeded or unavailable
      }
    }

    setIsSaving(false);
    setIsSavedSuccess(true);
    showToast(t('templates.output.saveSuccess'));

    setTimeout(() => {
      setIsSavedSuccess(false);
      setShowSaveModal(false);
    }, 1200);
  };

  const handleClearOutput = () => {
    if (content.length > 50 && typeof window !== 'undefined') {
      const confirmed = window.confirm(t('templates.output.clearConfirm'));
      if (!confirmed) return;
    }
    if (onClear) {
      onClear();
    }
  };

  return (
    <div className="relative flex flex-col h-full rounded-2xl border border-surface-border bg-surface shadow-glass overflow-hidden">
      {/* 1. Header Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-surface-border bg-surface-elevated/60">
        <div className="flex items-center gap-2 text-xs">
          <div className="flex items-center gap-1.5 font-semibold text-foreground">
            <SophiaIcon name="artificial-intelligence" size={15} className="text-primary shrink-0" />
            <span>{t('templates.outputTitle')}</span>
          </div>

          {isStreaming ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 animate-pulse">
              {t('templates.generatingOutput')}
            </span>
          ) : content ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
              {t('common.finish')}
            </span>
          ) : (
            <span className="text-[10px] text-foreground-muted">
              {t('common.ready' as any, undefined, 'Ready')}
            </span>
          )}

          {/* Mode Switcher Tabs (Formatted Markdown vs Raw) */}
          {content && !isStreaming && (
            <div className="flex items-center p-0.5 rounded-lg bg-surface border border-surface-border ml-2">
              <button
                type="button"
                onClick={() => setViewMode('formatted')}
                className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                  viewMode === 'formatted'
                    ? 'bg-primary text-primary-fg font-semibold shadow-sm'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
                title={t('templates.output.formattedPreview')}
              >
                <FileText className="w-3 h-3" />
                <span className="hidden sm:inline">{t('templates.output.formattedPreview')}</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('raw')}
                className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                  viewMode === 'raw'
                    ? 'bg-primary text-primary-fg font-semibold shadow-sm'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
                title={t('templates.output.rawMarkdown')}
              >
                <FileCode className="w-3 h-3" />
                <span className="hidden sm:inline">{t('templates.output.rawMarkdown')}</span>
              </button>
            </div>
          )}
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-1.5">
          {isStreaming && onStopStreaming && (
            <button
              type="button"
              onClick={onStopStreaming}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-rose-500 text-white hover:bg-rose-600 transition-colors shadow-sm"
            >
              <Square className="w-3 h-3 fill-current" />
              <span>{t('common.cancel')}</span>
            </button>
          )}

          {content && !isStreaming && (
            <>
              {/* Action 1: Copy */}
              <button
                type="button"
                onClick={handleCopy}
                title={t('templates.copyOutputBtn')}
                className="p-1.5 rounded-lg border border-surface-border text-foreground-muted hover:text-foreground hover:bg-muted/60 transition-colors"
                aria-label={t('templates.copyOutputBtn')}
              >
                {copied ? (
                  <Check className="w-4 h-4 text-emerald-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>

              {/* Action 2: Export Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowExportMenu((prev) => !prev)}
                  title={t('editor.export')}
                  className="p-1.5 rounded-lg border border-surface-border text-foreground-muted hover:text-foreground hover:bg-muted/60 transition-colors"
                  aria-label={t('editor.export')}
                >
                  <Download className="w-4 h-4" />
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 mt-1 w-48 rounded-xl border border-surface-border bg-surface p-1 shadow-xl z-20 space-y-0.5 text-xs animate-in fade-in duration-150">
                    <button
                      type="button"
                      onClick={() => handleExport('markdown')}
                      className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-muted text-foreground transition-colors flex items-center justify-between"
                    >
                      <span>{t('templates.output.exportMd')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExport('text')}
                      className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-muted text-foreground transition-colors flex items-center justify-between"
                    >
                      <span>{t('templates.output.exportTxt')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExport('html')}
                      className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-muted text-foreground transition-colors flex items-center justify-between"
                    >
                      <span>{t('templates.output.exportHtml')}</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Action 3: Save to Document Storage */}
              <button
                type="button"
                onClick={() => {
                  setDocTitle(templateTitle);
                  setShowSaveModal(true);
                }}
                title={t('common.save')}
                className="p-1.5 rounded-lg border border-surface-border text-foreground-muted hover:text-primary hover:bg-primary/10 transition-colors"
                aria-label={t('common.save')}
              >
                <Save className="w-4 h-4" />
              </button>

              {/* Action 4: Clear Output */}
              {onClear && (
                <button
                  type="button"
                  onClick={handleClearOutput}
                  title={t('templates.output.clearResult')}
                  className="p-1.5 rounded-lg border border-surface-border text-foreground-muted hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                  aria-label={t('templates.output.clearResult')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}

              {/* Action 5: Edit in Studio (Tiptap Editor) */}
              <button
                type="button"
                onClick={handleSendToTiptap}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-primary text-primary-fg hover:bg-primary-hover transition-all shadow-sm"
              >
                <FileEdit className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t('templates.sendToEditorBtn')}</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 z-30 px-3 py-1.5 rounded-xl bg-foreground text-background text-xs font-medium shadow-xl flex items-center gap-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* 2. Content Display Viewport */}
      <div className="flex-1 p-5 overflow-y-auto font-sans text-xs sm:text-sm text-foreground leading-relaxed">
        {content ? (
          viewMode === 'formatted' ? (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <MarkdownRenderer content={content} isStreaming={isStreaming} />
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[10px] text-foreground-muted font-mono pb-1 border-b border-surface-border">
                <span>{t('templates.output.rawMarkdown')}</span>
                <span>{content.length} {t('templates.output.charactersCount')}</span>
              </div>
              <pre className="whitespace-pre-wrap font-mono text-xs text-foreground bg-surface-elevated/40 p-4 rounded-xl border border-surface-border overflow-x-auto leading-relaxed">
                {content}
                {isStreaming && (
                  <span className="inline-block w-1.5 h-4 bg-primary animate-pulse ml-0.5 align-middle" />
                )}
              </pre>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center h-full min-h-[260px] text-center text-foreground-muted space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-muted/70 text-foreground-muted flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-primary/60" />
            </div>
            <div className="space-y-1 max-w-xs">
              <p className="font-semibold text-foreground text-xs">
                {t('templates.output.readyToGenerate')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 3. Footer Stats & Metadata Bar */}
      <div className="flex items-center justify-between p-2.5 px-4 border-t border-surface-border bg-surface-elevated/40 text-[11px] font-mono text-foreground-muted">
        <div className="flex items-center gap-3">
          <span>
            {words} {t('templates.output.wordsCount')}
          </span>
          <span>•</span>
          <span>
            {content.length} {t('templates.output.charactersCount')}
          </span>
          {tokensGenerated > 0 && (
            <>
              <span>•</span>
              <span className="text-primary font-bold">~{tokensGenerated} {t('templates.output.tokensCount')}</span>
            </>
          )}
        </div>

        <span className="hidden sm:inline text-[10px] text-foreground-muted/80">
          Edge-Stream v2.0 • Sophia Platform
        </span>
      </div>

      {/* 4. Structured Document Save Dialog Modal */}
      {showSaveModal && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150"
        >
          <div className="relative w-full max-w-md rounded-2xl border border-surface-border bg-surface p-5 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-foreground flex items-center gap-2">
                <FolderPlus className="w-4 h-4 text-primary" />
                {t('common.save')}
              </h4>
              <button
                type="button"
                onClick={() => setShowSaveModal(false)}
                className="p-1 rounded-lg text-foreground-muted hover:text-foreground"
                aria-label={t('common.close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="space-y-1">
                <label className="font-semibold text-foreground">{t('editor.documentTitle' as any, undefined, 'Document Title')}</label>
                <input
                  type="text"
                  value={docTitle}
                  onChange={(e) => setDocTitle(e.target.value)}
                  className="w-full p-2.5 rounded-xl border border-surface-border bg-surface text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-foreground">{t('editor.folder' as any, undefined, 'Folder')}</label>
                <select
                  value={docFolder}
                  onChange={(e) => setDocFolder(e.target.value)}
                  className="w-full p-2.5 rounded-xl border border-surface-border bg-surface text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                >
                  <option value="root">📁 Root Documents</option>
                  <option value="fld_marketing">📁 Marketing</option>
                  <option value="fld_seo">📁 SEO Blog</option>
                  <option value="fld_dev">📁 Technical Docs</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-foreground">{t('editor.tags' as any, undefined, 'Tags')}</label>
                <input
                  type="text"
                  value={docTags}
                  onChange={(e) => setDocTags(e.target.value)}
                  placeholder="tag1, tag2..."
                  className="w-full p-2.5 rounded-xl border border-surface-border bg-surface text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-surface-border">
              <button
                type="button"
                onClick={() => setShowSaveModal(false)}
                className="px-3.5 py-1.5 rounded-xl text-xs font-semibold border border-surface-border text-foreground hover:bg-muted"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveDocument}
                disabled={isSaving || isSavedSuccess}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-semibold bg-primary text-primary-fg hover:bg-primary-hover shadow-sm disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>{t('common.saving' as any, undefined, 'Saving...')}</span>
                  </>
                ) : isSavedSuccess ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    <span>{t('templates.output.saveSuccess')}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    <span>{t('common.confirm')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
